/**
 * pricing.ts — pure per-lot price model.
 *
 * MODEL (owner decision 2026-06-28, supersedes the 2026-06-25 Lot 410 concession-only model):
 * construction upgrades, net of the % discount, ARE part of the LFE contract price — buyers fund
 * them beyond any allowance — so they are SUMMED into the price, then financial incentives deduct:
 *
 *   Subtotal       = Base Purchase Price + Construction upgrades
 *   Incentives      = upgrade credit + base credit + (% off × upgrades)
 *   Contract price  = Subtotal − Incentives        (clamped ≥ 0; null when base is null)
 *
 * Example (Lot at $585,000 + $26,000 upgrades, 20% off upgrades): 585000 + 26000 − 5200 = $605,800.
 *
 * `contractPrice` (a.k.a. finalSalesPrice for back-compat) is what the buyer pays — the figure the
 * map's Contract-price lens, the dock, and the export all read.
 */

export interface PriceLine { name: string; value: number; unpriced: boolean }

export interface PriceBreakdown {
  base: number | null;            // GHL Base Purchase Price (the contract base)
  baseCredit: number;             // genuine price reduction applied to the price
  contractPrice: number | null;   // = base + upgrades − incentives (what the buyer pays)
  finalSalesPrice: number | null; // back-compat alias of contractPrice (lens/export)
  upgradeSubtotal: number;        // priced value of selected upgrades — SUMMED into the price
  upgradeLines: PriceLine[];
  upgradeCredit: number;          // flat upgrade-scoped credit (deducts)
  pctOff: number;                 // % off upgrades (deducts pctOff × upgradeSubtotal)
  upgradeDiscount: number;        // = round(upgradeSubtotal × pctOff / 100) — the % off in dollars
  incentiveTotal: number;         // = upgradeCredit + baseCredit + upgradeDiscount (total deducted)
}

export interface ComputeArgs {
  base: number | null;
  model: string;
  constructionUpgrades: string;
  upgradePricing: Map<string, number>;
  customPrices?: Map<string, number>;
  upgradeCredit?: number | null;
  pctOff?: number | null;
  baseCredit?: number | null;
}

const num = (v: number | null | undefined): number =>
  typeof v === 'number' && isFinite(v) ? v : 0;

const cents = (v: number): number => Math.round(v * 100) / 100;

// Split ONLY on newlines — the source string is newline-joined and upgrade NAMES contain
// commas ("Attic - Finished (2BR, Bath, Staircase)"). Splitting on commas would shred that
// name into unmatchable fragments that all render "(unpriced)".
export const upgradeItems = (s: string): string[] =>
  (s || '').split(/\n+/).map(x => x.trim()).filter(Boolean);

export function computeLotPrice(args: ComputeArgs): PriceBreakdown {
  const custom = args.customPrices;
  const upgradeLines: PriceLine[] = upgradeItems(args.constructionUpgrades).map((name) => {
    const v = custom?.get(name) ?? args.upgradePricing.get(`${args.model}|${name}`);
    return { name, value: v ?? 0, unpriced: v == null };
  });
  const upgradeSubtotal = cents(upgradeLines.reduce((s, l) => s + l.value, 0));

  const baseCredit = num(args.baseCredit);
  const upgradeCredit = num(args.upgradeCredit);
  const pctOff = num(args.pctOff);
  const base = typeof args.base === 'number' && isFinite(args.base) ? args.base : null;
  // Contract price = base + construction upgrades, less financial incentives (upgrade credit, % off
  // upgrades, base credit). Owner model 2026-06-28: upgrades net of the % discount are part of the
  // price (buyers fund them beyond the allowance), so they're summed in, then incentives deduct.
  const upgradeDiscount = cents(upgradeSubtotal * pctOff / 100);
  const incentiveTotal = cents(upgradeCredit + baseCredit + upgradeDiscount);
  const contractPrice = base == null ? null : Math.max(0, cents(base + upgradeSubtotal - incentiveTotal));

  return {
    base,
    baseCredit,
    contractPrice,
    finalSalesPrice: contractPrice,
    upgradeSubtotal,
    upgradeLines,
    upgradeCredit,
    pctOff,
    upgradeDiscount,
    incentiveTotal,
  };
}
